import { useEffect, useState } from 'react';

import axios from 'axios';

import { activityTypes } from 'bundles/apex/activities/type/config';
import { checkDateChangeForImmediateReminders } from 'bundles/apex/reminders/helpers';
import { get } from 'helpers/api';
import { getParentOptionValue } from 'bundles/crm/parent_helpers';
import { serializeTaskForm } from '../serializers';

import type { Dependencies, ForUser, ModalOptions, Task, TaskAttributes, TaskFormState } from '../types';
import type { Reminder } from 'bundles/apex/reminders/types';

import 'bundles/apex/tasks/styles.scss';
import { showSnackbar } from 'components/ui/snackbar/helper';

export interface Props {
  allowParentSelection?: boolean;
  checkImmediateRemindersPath?: string;
  createTask?: (task: TaskAttributes) => Promise<any>;
  formDependenciesPath?: string;
  forUser?: ForUser;
  reminders?: Reminder[];
  show?: boolean;
  updateTask?: (task: TaskAttributes) => Promise<any>;
  options?: ModalOptions;
  onHide: () => void;
  onSave?: () => void;
}

type TaskSaveSerializer = () => Promise<Partial<TaskAttributes> | null>;

let dependenciesAbortController;

export function useTaskModalState({
  allowParentSelection,
  checkImmediateRemindersPath,
  createTask,
  formDependenciesPath,
  forUser,
  options,
  reminders,
  show,
  updateTask,
  onHide,
  onSave
}: Props) {
  const [dependencies, setDependencies] = useState<Dependencies | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(true);
  const [formTask, setFormTask] = useState<TaskFormState>({ contactsTasks: [] });
  const [requestReminders, setRequestReminders] = useState<Reminder[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[] | null>();
  const [immediateReminders, setImmediateReminders] = useState<any[]>([]);
  const [userNames, setUserNames] = useState<any[]>([]);
  const [recheckReminders, setRecheckReminders] = useState(false);
  const [originalDeadline, setOriginalDeadline] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [contactsTasks, setContactsTasks] = useState(formTask?.contactsTasks || []);

  async function fetchDependencies(parentParams?: { parent_id: number, parent_type: string }) {
    dependenciesAbortController?.abort();
    dependenciesAbortController = new AbortController();

    setLoading(true);

    try {
      const dependencies = await get(formDependenciesPath || '/apex/tasks/form_dependencies', {
        signal: dependenciesAbortController.signal,
        params: parentParams,
      });

      setDependencies(dependencies);
      setInitializing(false);
      setLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        showSnackbar({ text: 'Something went wrong', variant: 'danger' });
        hide();
      }
    }
  }

  useEffect(() => {
    // TODO: Smart caching would be nice - RTK Query? Custom hook? Need to be able to invalidate on date change...
    if (show) {
      fetchDependencies();
    }
  }, [formDependenciesPath, show]);

  useEffect(() => {
    if (!show || !allowParentSelection || formTask.id || !formTask.parentOptionValue) return;

    try {
      const { parentId, parentType } = JSON.parse(formTask.parentOptionValue);
      if (parentId && parentType) {
        fetchDependencies({ parent_id: parentId, parent_type: parentType });
      }
    } catch {
      // Ignore malformed parent values
    }
  }, [show, allowParentSelection, formTask.id, formTask.parentOptionValue]);

  function hide() {
    setInitializing(true);
    onHide();
  }

  async function reset(task: Task | null = null) {
    let deadline = task?.deadline;
    if (!deadline) {
      if (dependencies?.deal) {
        deadline = { operator: 'before', units: 'day' };
      } else {
        deadline = { date: '', static: true };
      }
    }

    const formTask: TaskFormState = {
      ...task,
      assigneeId: task ? task.assignee?.id : forUser?.id,
      contactsTasks: task?.contactsTasks?.map(contactsTask => ({ ...contactsTask, localId: contactsTask.id })) || [],
      deadline,
      requiresAttachment: !!task?.requiresAttachment,
      reminders: reminders || [],
    };

    if (task) {
      formTask.parentOptionValue = getParentOptionValue(task);
    } else if (options?.parents?.length) {
      formTask.parentOptionValue = options.parents[0].value;
    } else if (allowParentSelection) {
      formTask.selectedParentType = 'Apex::Deal';
    }

    setFormTask(formTask);
    setOriginalDeadline(formTask.deadline);

    setRequestReminders(reminders?.filter(reminder => !reminder.sent) || []);
    setImmediateReminders([]);
    setRecheckReminders(false);
    setUserNames([]);
    setErrors(null);
    setHasChanges(Boolean(task && !task.id));
  }

  function handleRemindersChange(updatedReminders: Reminder[]) {
    setHasChanges(true);
    setRecheckReminders(true);
    setRequestReminders(updatedReminders);
  }

  async function submitTask(serializeTask: TaskSaveSerializer) {
    setSaving(true);

    try {
      const task = await serializeTask();

      if (!task) {
        return;
      }

      let result;
      if (formTask.id && updateTask) {
        result = await updateTask(task);
      } else if (!formTask.id && createTask) {
        result = await createTask(task);
      }

      setRequestReminders(filterReminders(result?.reminders || []));
      setImmediateReminders([]);
      setContactsTasks(result?.contactsTasks || []);
      setHasChanges(false);
      setSaving(false);
      setErrors(null);
      if (result) onSave?.();
      return result;
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
      return { errors: errors };
    }
  }

  const filterReminders = (reminders: Reminder[]) => {
    const immediateReminderIds = immediateReminders.map(reminder => reminder.id);
    let filteredReminders = reminders;

    if (immediateReminderIds.length > 0) {
      filteredReminders = filteredReminders.filter(reminder => !immediateReminderIds.includes(reminder.id));
    }

    if (forUser) {
      filteredReminders = filteredReminders.filter(reminder => reminder.userId === forUser.id);
    }

    return filteredReminders;
  };

  async function checkForImmediateReminders(reminders: Reminder[], deadline = formTask.deadline) {
    const result = await checkDateChangeForImmediateReminders(
      checkImmediateRemindersPath,
      deadline,
      requestReminders
    );

    if (result.reminders.length > 0) {
      setImmediateReminders(result.reminders);
      setUserNames(result.users);
      setSaving(false);
      return true;
    }

    return false;
  }

  async function handleSave(
    changes: Partial<TaskFormState>,
    keepReminders: boolean | null | undefined = null,
  ) {
    if (allowParentSelection && !formTask.id && !formTask.parentOptionValue) {
      setErrors(['Source is required']);
      return { errors: ['Source is required'] };
    }

    const processChanges = async () => {
      if (formTask.id) {
        let hasDeadlineChanged = false;
        if (changes.deadline && originalDeadline) {
          hasDeadlineChanged = JSON.stringify(originalDeadline) !== JSON.stringify(changes.deadline);
        }
        if ((recheckReminders || hasDeadlineChanged) && keepReminders == null && checkImmediateRemindersPath) {
          const hasImmediateReminders = await checkForImmediateReminders(
            changes.reminders || requestReminders,
            changes.deadline
          );
          setRecheckReminders(false);
          if (hasImmediateReminders) {
            return null;
          }
        }
      }

      const reminders = requestReminders;
      const task = serializeTaskForm(
        { ...changes, parentOptionValue: formTask?.parentOptionValue },
        { keepReminders, reminders }
      );
      if (formTask?.id && formTask?.path) {
        task.id = formTask.id;
        task.path = formTask.path;
      } else if (!formTask?.id && !formTask?.name) {
        task.name ||= 'New Task';
      }
      return task;
    };
    return submitTask(processChanges);
  }

  function handleChange(changes) {
    // Check if deadline is being changed
    if (changes.deadline && originalDeadline) {
      const hasChanged = JSON.stringify(originalDeadline) !== JSON.stringify(changes.deadline);
      setRecheckReminders(hasChanged);
    }

    // When the user switches Source type, drop any stale parent selection.
    if (changes.selectedParentType && changes.selectedParentType !== formTask.selectedParentType) {
      changes = { ...changes, parentOptionValue: undefined };
    }

    setHasChanges(true);
    setFormTask({ ...formTask, ...changes });
  }

  const typeOptions = [
    { label: 'None', value: '' },
    ...Object.values(activityTypes).map(({ key, label }) => ({
      label,
      value: key,
    })),
  ];

  return ({
    contactsTasks,
    dependencies,
    errors,
    formTask,
    handleSave,
    handleChange,
    handleRemindersChange,
    hasChanges,
    hide,
    immediateReminders,
    initializing,
    loading,
    reset,
    requestReminders,
    saving,
    setContactsTasks,
    setImmediateReminders,
    typeOptions,
    userNames,
  });
}
